import { useLocale as useLocaleR42 } from '@/lib/locale';
import React, { useEffect, useRef, useState } from 'react';
import { PlayCircle, Volume2 } from 'lucide-react';

// -----------------------------------------------------------------------------
// TutorialShowcase — the full 3-minute animated coach tutorial, shown INLINE on
// the landing page right before the pricing plans. Visitors see a real cover
// frame from the video with a gold "SplashPad in 3 minutes" button on top and
// must click to play (the ~136 MB MP4 streams from the CDN only on demand —
// zero page-load cost).
//
// Swapping in a new video version later = update TUTORIAL_VIDEO_SRC below and
// (optionally) regenerate the cover: ffmpeg -ss 8 -i video.mp4 -frames:v 1
// -vf scale=1600:-2 -q:v 4 frontend/public/videos/tutorial-cover.jpg
// -----------------------------------------------------------------------------

const TUTORIAL_VIDEO_SRC =
    'https://customer-assets-agu9un31.emergentagent.net/wingman/9bc9dc43-c5c8-4137-ba8e-3b945fe05ebf/attachments/08ee4be32e7f4ed6b9e162ae4b4ef09d_splashpad-coach-tutorial.mp4';
const TUTORIAL_POSTER = '/videos/tutorial-cover.jpg';

// -----------------------------------------------------------------------------
// TutorialAutoplayCard — compact "closing sales" version used on the SIGNUP
// page, right above the payment section. Starts playing automatically (muted —
// browsers refuse autoplay with sound) once ~40% of it scrolls into view, with
// a prominent "Tap for sound" pill. The idea: even coaches who skipped the
// landing-page video see everything they're buying at the exact moment they
// enter card details.
// -----------------------------------------------------------------------------
export const TutorialAutoplayCard = () => {
    const { t: t42 } = useLocaleR42();
    const videoRef = useRef(null);
    const [muted, setMuted] = useState(true);
    const [started, setStarted] = useState(false);

    useEffect(() => {
        const el = videoRef.current;
        if (!el || typeof IntersectionObserver === 'undefined') return undefined;
        const io = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting && entry.intersectionRatio >= 0.4) {
                        // Muted autoplay is universally allowed; ignore rejection anyway.
                        const p = el.play();
                        if (p && p.catch) p.catch(() => { /* ignore */ });
                        setStarted(true);
                        io.disconnect();
                    }
                });
            },
            { threshold: [0.4] }
        );
        io.observe(el);
        return () => io.disconnect();
    }, []);

    const unmute = () => {
        const el = videoRef.current;
        if (el) {
            el.muted = false;
            if (el.paused) {
                const p = el.play();
                if (p && p.catch) p.catch(() => { /* ignore */ });
            }
        }
        setMuted(false);
    };

    return (
        <div className="overflow-hidden rounded-xl border border-[#0a1628]/15 bg-[#0a1628]" data-testid="signup-tutorial-video-card">
            <div className="relative aspect-video w-full bg-black">
                <video
                    ref={videoRef}
                    className="h-full w-full"
                    src={TUTORIAL_VIDEO_SRC}
                    poster={TUTORIAL_POSTER}
                    muted
                    playsInline
                    controls
                    preload="metadata"
                    data-testid="signup-tutorial-video"
                > {t42("Your browser does not support embedded videos.")} </video>
                {muted && started && (
                    <button
                        type="button"
                        onClick={unmute}
                        className="sd-focus absolute right-3 top-3 inline-flex items-center gap-1.5 rounded-full bg-[#ffd700] px-3.5 py-1.5 text-xs font-bold text-[#0a1628] shadow-lg transition-transform duration-150 hover:scale-105"
                        data-testid="signup-tutorial-unmute-button"
                    >
                        <Volume2 className="h-3.5 w-3.5" /> {t42("Tap for sound")} </button>
                )}
            </div>
            <p className="px-3 py-2 text-[11px] font-medium text-white/80"> {t42("Everything SplashPad does for your business — in 3 minutes.")} </p>
        </div>
    );
};

export const TutorialShowcase = () => {
    const { t: t42 } = useLocaleR42();
    const [playing, setPlaying] = useState(false);

    return (
        <section className="bg-[#f4f7fb] py-16 lg:py-24" data-testid="tutorial-showcase-section">
            <div className="mx-auto max-w-5xl px-4 sm:px-6">
                <div className="mx-auto mb-10 max-w-3xl text-center">
                    <h2 className="font-display text-3xl font-bold text-[#0a1628] sm:text-4xl"> {t42("See everything SplashPad can do")} </h2>
                    <p className="mt-3 text-[#1a3a5c]"> {t42("One video, every feature — watch Splash walk through the whole platform, then pick the plan that fits below.")} </p>
                </div>

                {/* Cinema card: dark navy frame around the 16:9 player */}
                <div className="overflow-hidden rounded-3xl bg-[#0a1628] p-2 shadow-2xl sm:p-3">
                    <div className="relative aspect-video w-full overflow-hidden rounded-2xl bg-black">
                        {playing ? (
                            <video
                                className="h-full w-full"
                                src={TUTORIAL_VIDEO_SRC}
                                poster={TUTORIAL_POSTER}
                                controls
                                autoPlay
                                playsInline
                                preload="none"
                                data-testid="tutorial-inline-video"
                            > {t42("Your browser does not support embedded videos.")} </video>
                        ) : (
                            <button
                                type="button"
                                onClick={() => setPlaying(true)}
                                className="sd-focus group relative block h-full w-full cursor-pointer"
                                data-testid="tutorial-play-button"
                                aria-label={t42("Play the SplashPad tutorial video — every feature in 3 minutes")}
                            >
                                <img
                                    src={TUTORIAL_POSTER}
                                    alt="Splash, the SplashPad mascot, ready to walk you through every feature"
                                    className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-[1.02]"
                                    loading="lazy"
                                />
                                {/* Soft bottom vignette so the caption stays readable */}
                                <span className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#0a1628]/75 via-transparent to-transparent" />
                                <span className="absolute inset-0 flex items-center justify-center">
                                    <span className="inline-flex items-center gap-2.5 rounded-full bg-[#ffd700] px-7 py-3.5 text-base font-bold text-[#0a1628] shadow-[0_8px_30px_rgba(255,215,0,0.35)] transition-transform duration-200 group-hover:scale-105 sm:text-lg">
                                        <PlayCircle className="h-6 w-6" /> {t42("SplashPad in 3 minutes")} </span>
                                </span>
                                <span className="pointer-events-none absolute inset-x-0 bottom-4 text-center text-sm font-medium text-white/85"> {t42("Every feature, shown for real — click to play")} </span>
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
};
